import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';
import { config } from '../config';

export const userManager = config.authMode === 'keycloak' ? new UserManager({
  authority: config.keycloakAuthority,
  client_id: config.keycloakClientId,
  redirect_uri: `${window.location.origin}/auth/callback`,
  post_logout_redirect_uri: window.location.origin,
  response_type: 'code', scope: 'openid profile email',
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  // AuthProvider renews on the expiring event through the same single-flight
  // path used by requests, avoiding competing refresh-token operations.
  automaticSilentRenew: false,
  accessTokenExpiringNotificationTimeInSeconds: 60
}) : null;

let renewing: Promise<User | null> | null = null;
export async function currentSession(forceRenew = false): Promise<User | null> {
  if (!userManager) return null;
  const user = await userManager.getUser();
  if (!user) return null;
  if (!forceRenew && !user.expired && (user.expires_in ?? 0) > 30) return user;
  // Sleeping tabs can miss renewal timers. Check before requests, and share one
  // refresh operation so concurrent requests do not reuse a rotating refresh token.
  if (!renewing) renewing = userManager.signinSilent().then(async renewed => {
    if (!renewed || renewed.expired) throw new Error('Session expired');
    return renewed;
  }).catch(async () => {
    await userManager!.removeUser();
    return null;
  }).finally(() => { renewing = null; });
  return renewing;
}

export async function requestToken(fallback: string, forceRenew = false): Promise<string> {
  if (!userManager) return fallback;
  const user = await currentSession(forceRenew);
  if (!user) throw new Error('Your session has ended. Please sign in again.');
  return user.access_token;
}

export async function endSession() { await userManager?.removeUser(); }
